import { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Chip, IconButton, Alert,
  CircularProgress, Tab, Tabs, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import {
  PersonAdd, Edit, Delete, Schedule, People, CheckCircle, Cancel
} from '@mui/icons-material';
import { therapistAPI } from '../services/api';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function TherapistManagement() {
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [filterActive, setFilterActive] = useState('true');

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentTherapist, setCurrentTherapist] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialization: '',
    qualification: '',
    experienceYears: 0,
    isActive: true,
    notes: '',
    workingHours: []
  });

  useEffect(() => {
    loadTherapists();
  }, [filterActive]);

  const loadTherapists = async () => {
    setLoading(true);
    try {
      const response = await therapistAPI.getAll({ active: filterActive });
      setTherapists(response.data.data || []);
    } catch (err) {
      setError('Failed to load therapists');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (therapist = null) => {
    if (therapist) {
      setEditMode(true);
      setCurrentTherapist(therapist);
      setFormData({
        firstName: therapist.first_name,
        lastName: therapist.last_name,
        email: therapist.email,
        phone: therapist.phone,
        specialization: therapist.specialization || '',
        qualification: therapist.qualification || '',
        experienceYears: therapist.experience_years || 0,
        isActive: therapist.is_active,
        notes: therapist.notes || '',
        workingHours: therapist.working_hours?.length > 0
          ? therapist.working_hours
          : DAYS_OF_WEEK.map(day => ({ dayOfWeek: day, startTime: '09:00', endTime: '17:00' }))
      });
    } else {
      setEditMode(false);
      setCurrentTherapist(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        specialization: '',
        qualification: '',
        experienceYears: 0,
        isActive: true,
        notes: '',
        workingHours: DAYS_OF_WEEK.map(day => ({
          dayOfWeek: day,
          startTime: '09:00',
          endTime: '17:00'
        }))
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentTherapist(null);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await therapistAPI.update(currentTherapist.id, formData);
        setSuccess('Therapist updated successfully');
      } else {
        await therapistAPI.create(formData);
        setSuccess('Therapist created successfully');
      }
      handleCloseDialog();
      loadTherapists();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save therapist');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this therapist?')) {
      return;
    }

    try {
      await therapistAPI.delete(id);
      setSuccess('Therapist deactivated successfully');
      loadTherapists();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to deactivate therapist');
    }
  };

  const handleWorkingHourChange = (index, field, value) => {
    const newWorkingHours = [...formData.workingHours];
    newWorkingHours[index][field] = value;
    setFormData({ ...formData, workingHours: newWorkingHours });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      <Card elevation={2}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <People sx={{ fontSize: 40, color: 'primary.main' }} />
              <Typography variant="h5" fontWeight="bold">
                Therapist Management
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => handleOpenDialog()}
            >
              Add Therapist
            </Button>
          </Box>

          <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ mb: 2 }}>
            <Tab label="All Therapists" />
            <Tab label={`Active (${therapists.filter(t => t.is_active).length})`} onClick={() => setFilterActive('true')} />
            <Tab label={`Inactive (${therapists.filter(t => !t.is_active).length})`} onClick={() => setFilterActive('false')} />
          </Tabs>

          <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Contact</strong></TableCell>
                  <TableCell><strong>Specialization</strong></TableCell>
                  <TableCell><strong>Experience</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Treatments</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {therapists.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">No therapists found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  therapists.map((therapist) => (
                    <TableRow key={therapist.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {therapist.first_name} {therapist.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {therapist.qualification}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{therapist.phone}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {therapist.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{therapist.specialization}</Typography>
                      </TableCell>
                      <TableCell>{therapist.experience_years} years</TableCell>
                      <TableCell>
                        <Chip
                          label={therapist.is_active ? 'Active' : 'Inactive'}
                          color={therapist.is_active ? 'success' : 'default'}
                          size="small"
                          icon={therapist.is_active ? <CheckCircle /> : <Cancel />}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {therapist.completed_treatments}/{therapist.total_treatments}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Completed/Total
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(therapist)}
                          title="Edit"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(therapist.id)}
                          title="Deactivate"
                          disabled={!therapist.is_active}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Therapist Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Therapist' : 'Add New Therapist'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Specialization"
                value={formData.specialization}
                onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                placeholder="e.g., Massage Therapy, Acupuncture"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Qualification"
                value={formData.qualification}
                onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Experience (Years)"
                value={formData.experienceYears}
                onChange={(e) => setFormData({ ...formData, experienceYears: parseInt(e.target.value) })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                Working Hours
              </Typography>
              {formData.workingHours.map((hour, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                  <Grid item xs={4}>
                    <Typography variant="body2" sx={{ pt: 2 }}>{hour.dayOfWeek}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="time"
                      label="Start Time"
                      value={hour.startTime}
                      onChange={(e) => handleWorkingHourChange(index, 'startTime', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      type="time"
                      label="End Time"
                      value={hour.endTime}
                      onChange={(e) => handleWorkingHourChange(index, 'endTime', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TherapistManagement;